import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useFormik } from 'formik'
import { AuthLayout, AuthLayoutActions } from '../../layouts/Auth'
import { startEmailLogin, authenticateLogin } from '../../requests/session'
import { startEmailLoginValidation } from '../../../server/validation/session'
import { Input } from '../../components/Inputs/Input'
import { handleError } from '../../errors/handleError'
import { useAuth } from '../../contexts/useAuthUser'
import { OAuthButtons } from './components/OAuthButtons'

export const Login: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const { authUser, loading, checkAuth } = useAuth()
  const [initializing, setInitializing] = useState(true)

  const formik = useFormik({
    initialValues: {
      email: ''
    },
    validationSchema: startEmailLoginValidation,
    onSubmit: async (values) => {
      try {
        await startEmailLogin(values)

        navigate('/confirm', {
          state: {
            emailUsed: values.email
          }
        })
      } catch (err) {
        handleError(err, 'Whoops, something went wrong logging you in. Please try again later.')
      }
    }
  })

  useEffect(() => {
    if (loading) {
      return
    }

    if (authUser) {
      return navigate('/app/content')
    } 

    const checkIfNewSession = async () => {
      const params = new URLSearchParams(location.search)
      const token = params.get('token')
      const method = params.get('stytch_token_type')

      if (!token || !method) {
        setInitializing(false)
        return
      }

      try {
        toast.info('Logging you in...')
        const res = await authenticateLogin({ token, method })
        localStorage.setItem('auth-token', res.data.sessionJwt)

        await checkAuth()

        if (res.data.isNewUser) {
          toast.success('🚀 Welcome! Start your first inbox!')
          return navigate('/app/inboxes?isNewUser=true')
        } else {
          toast.success('Welcome back!')
        }

        return navigate('/app/content')
      } catch (err) {
        handleError(err, 'Whoops, something went wrong logging you in. Please try again later.')
      }

      setInitializing(false)
    }

    checkIfNewSession()
  }, [loading, authUser])

  useEffect(() => {
    if (!initializing) {
      const params = new URLSearchParams(location.search)
      const emailParam = params.get('email')

      if (emailParam) {
        formik.setFieldValue('email', emailParam).then(() => {
          formik.submitForm()
        })
      }
    }
  }, [initializing, location])

  if (initializing) {
    return (
      <AuthLayout>
        <h1>One moment please...</h1>
        <h3>We are brewing coffee</h3>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <h1>Welcome</h1>

      <form onSubmit={formik.handleSubmit}>
        <Input
          label="Enter your email address"
          placeholder="bruce@wayne.com"
          name="email"
          type="email"
          formik={formik}
          disabled={formik.isSubmitting || initializing}
        />
        <AuthLayoutActions>
          <button type="submit" className="primary" disabled={formik.isSubmitting || initializing}>
                      Next
          </button>
        </AuthLayoutActions>
      </form>
      <OAuthButtons />
    </AuthLayout>
  )
}
